import { Player, Team } from '@/types/player';
import { MatchPhase, MatchState, RoundResult } from '@/types/game';
import { LIFE_POINTS_CONFIG } from '@/utils/constants';
import { GameRules } from './GameRules';

/**
 * マッチ状態管理クラス
 * 複数ラウンドにわたる試合の持ち点・勝敗を管理
 */
export class MatchStateManager {
  private state: MatchState;
  private teamMapping: Record<string, Team> = {};

  constructor() {
    this.state = this.createInitialState();
  }

  /**
   * 初期状態を作成
   */
  private createInitialState(): MatchState {
    return {
      phase: MatchPhase.IN_PROGRESS,
      lifePoints: {},
      roundNumber: 1,
      roundHistory: [],
      winner: null,
      defeatedPlayerId: null
    };
  }

  /**
   * マッチを初期化
   * @param players プレイヤー配列
   */
  initializeMatch(players: Player[]): void {
    const { INITIAL_POINTS } = LIFE_POINTS_CONFIG;

    // チームマッピングを保存
    this.teamMapping = {};
    for (const player of players) {
      this.teamMapping[player.id] = player.team;
    }

    // 初期持ち点を設定
    const lifePoints: Record<string, number> = {};
    for (const player of players) {
      if (player.team === Team.TEAM_2) {
        // Team 2（1人側）: 80点
        lifePoints[player.id] = INITIAL_POINTS.TEAM_2_SOLO;
      } else {
        // Team 1（2人側）: 各60点
        lifePoints[player.id] = INITIAL_POINTS.TEAM_1_DUO;
      }
    }

    this.state = {
      phase: MatchPhase.IN_PROGRESS,
      lifePoints,
      roundNumber: 1,
      roundHistory: [],
      winner: null,
      defeatedPlayerId: null
    };
  }

  /**
   * ラウンド結果を適用
   * @param roundWinner ラウンド勝者チーム
   * @param players プレイヤー配列（手札情報含む）
   * @returns ラウンド結果
   */
  applyRoundResult(roundWinner: Team, players: Player[]): RoundResult {
    const losingTeam = roundWinner === Team.TEAM_1 ? Team.TEAM_2 : Team.TEAM_1;
    const losingPlayers = players.filter(p => p.team === losingTeam);

    // ダメージ計算
    const damageDealt: Record<string, number> = {};
    const remainingCards: Record<string, number> = {};

    for (const player of losingPlayers) {
      const damage = GameRules.calculateDamage(player.hand);
      damageDealt[player.id] = damage;
      remainingCards[player.id] = player.hand.length;

      // 持ち点からダメージを引く
      this.state.lifePoints[player.id] -= damage;
    }

    // 勝者チームのプレイヤーも残り手札を記録
    const winningPlayers = players.filter(p => p.team === roundWinner);
    for (const player of winningPlayers) {
      damageDealt[player.id] = 0;
      remainingCards[player.id] = player.hand.length;
    }

    // ラウンド結果を作成
    const roundResult: RoundResult = {
      roundNumber: this.state.roundNumber,
      winner: roundWinner,
      damageDealt,
      remainingCards
    };

    // 履歴に追加
    this.state.roundHistory.push(roundResult);

    // 持ち点が0以下のプレイヤーをチェック
    const defeatedPlayer = this.findDefeatedPlayer();
    if (defeatedPlayer) {
      this.state.defeatedPlayerId = defeatedPlayer.id;
      this.state.phase = MatchPhase.FINISHED;
      // 敗北したプレイヤーの相手チームが勝者
      this.state.winner = defeatedPlayer.team === Team.TEAM_1 ? Team.TEAM_2 : Team.TEAM_1;
    }

    return roundResult;
  }

  /**
   * 持ち点が0以下のプレイヤーを探す
   */
  private findDefeatedPlayer(): { id: string; team: Team } | null {
    for (const [playerId, points] of Object.entries(this.state.lifePoints)) {
      if (points <= 0) {
        // 保存されたチームマッピングからチームを取得
        const team = this.teamMapping[playerId];
        if (team !== undefined) {
          return { id: playerId, team };
        }
      }
    }
    return null;
  }

  /**
   * 次のラウンドを開始
   */
  startNextRound(): void {
    if (this.state.phase === MatchPhase.FINISHED) {
      return;
    }
    this.state.roundNumber += 1;
  }

  /**
   * マッチが終了しているか確認
   */
  isMatchFinished(): boolean {
    return this.state.phase === MatchPhase.FINISHED;
  }

  /**
   * マッチ勝者を取得
   */
  getMatchWinner(): Team | null {
    return this.state.winner;
  }

  /**
   * プレイヤーの持ち点を取得
   * @param playerId プレイヤーID
   */
  getLifePoints(playerId: string): number {
    return this.state.lifePoints[playerId] ?? 0;
  }

  /**
   * プレイヤーの持ち点を設定（ネットワーク同期用）
   * @param playerId プレイヤーID
   * @param points 持ち点
   */
  setLifePoints(playerId: string, points: number): void {
    this.state.lifePoints[playerId] = points;
  }

  /**
   * 全プレイヤーの持ち点を取得
   */
  getAllLifePoints(): Record<string, number> {
    return { ...this.state.lifePoints };
  }

  /**
   * 現在のラウンド番号を取得
   */
  getRoundNumber(): number {
    return this.state.roundNumber;
  }

  /**
   * ラウンド履歴を取得
   */
  getRoundHistory(): RoundResult[] {
    return [...this.state.roundHistory];
  }

  /**
   * 現在のマッチ状態を取得
   */
  getState(): MatchState {
    return { ...this.state };
  }

  /**
   * 状態をリセット
   */
  reset(): void {
    this.state = this.createInitialState();
  }
}
